/* HALO product page: swipe gallery, colour swatches, Buy 1 / Buy 2 with a colour per device,
   delivery estimate and a sticky add-to-cart bar. Prices come pre-formatted from Liquid so the
   visitor's currency is always right. */
if (!customElements.get('halo-product')) {
  class HaloProduct extends HTMLElement {
    connectedCallback() {
      const json = this.querySelector('[data-hp-data]');
      if (!json) return;
      this.data = JSON.parse(json.textContent);
      this.state = { pack: this.dataset.defaultPack || '1', c1: this.data.initialColour, c2: this.data.initialColour, index: 0 };

      this.track = this.querySelector('[data-hp-track]');
      this.slides = Array.from(this.querySelectorAll('[data-hp-slide]'));
      this.dots = Array.from(this.querySelectorAll('.hp-gallery__dots li'));
      this.thumbs = Array.from(this.querySelectorAll('[data-hp-thumb]'));
      this.atc = this.querySelector('[data-hp-atc]');
      this.errorEl = this.querySelector('[data-hp-error]');
      this.sticky = this.querySelector('[data-hp-sticky]');

      this.addEventListener('click', (e) => this.onClick(e));
      this.addEventListener('keydown', (e) => {
        const offer = e.target.closest('[data-hp-offer]');
        if (offer && (e.key === 'Enter' || e.key === ' ') && e.target === offer) {
          e.preventDefault();
          this.state.pack = offer.dataset.pack;
          this.render();
        }
      });
      this.atc && this.atc.addEventListener('click', () => this.addToCart());
      const stickyBtn = this.querySelector('[data-hp-sticky-atc]');
      stickyBtn && stickyBtn.addEventListener('click', () => this.addToCart());

      this.setupGallery();
      this.setupSticky();
      this.renderDelivery();
      this.render();
    }

    /* ---------- gallery ---------- */
    setupGallery() {
      this.markIndex(0);
      if (!this.track || this.slides.length < 2) return;
      let ticking = false;
      this.track.addEventListener('scroll', () => {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(() => {
          ticking = false;
          const w = this.track.clientWidth || 1;
          const i = Math.round(this.track.scrollLeft / w);
          if (i !== this.state.index) this.markIndex(i);
        });
      }, { passive: true });
    }

    goTo(i) {
      if (!this.track || !this.slides.length) return;
      const count = this.slides.length;
      const index = (i + count) % count;
      this.track.scrollTo({ left: index * this.track.clientWidth, behavior: 'smooth' });
      this.markIndex(index);
    }

    markIndex(i) {
      this.state.index = i;
      this.dots.forEach((d, n) => d.classList.toggle('is-active', n === i));
      this.thumbs.forEach((t, n) => t.setAttribute('aria-current', n === i ? 'true' : 'false'));
    }

    showVariantImage(colour) {
      const v = this.variant(colour, '1');
      if (!v || !v.large || !this.slides.length) return;
      // The packshot slide always shows the colour picked; other colour packshots stay out of the gallery.
      let index = this.slides.findIndex((s) => s.hasAttribute('data-hp-packshot'));
      if (index < 0) index = 0;
      const img = this.slides[index].querySelector('img');
      if (img) { img.removeAttribute('srcset'); img.removeAttribute('sizes'); img.src = v.large; img.alt = colour; }
      const thumb = this.querySelector('[data-hp-packshot-thumb] img') || (this.thumbs[index] && this.thumbs[index].querySelector('img'));
      if (thumb) { thumb.removeAttribute('srcset'); thumb.src = v.image; }
      this.goTo(index);
    }

    /* ---------- sticky bar ---------- */
    setupSticky() {
      if (!this.sticky || !this.atc || !('IntersectionObserver' in window)) return;
      const io = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          const past = !entry.isIntersecting && entry.boundingClientRect.top < 0;
          this.sticky.classList.toggle('is-visible', past);
          this.sticky.setAttribute('aria-hidden', past ? 'false' : 'true');
          const btn = this.sticky.querySelector('button');
          if (btn) btn.tabIndex = past ? 0 : -1;
        });
      });
      io.observe(this.atc);
    }

    /* ---------- delivery estimate (business days, from the shipping policy) ---------- */
    renderDelivery() {
      const el = this.querySelector('[data-hp-delivery]');
      if (!el) return;
      const min = parseInt(el.dataset.min, 10) || 3;
      const max = parseInt(el.dataset.max, 10) || 7;
      const add = (days) => {
        const d = new Date();
        let n = 0;
        while (n < days) {
          d.setDate(d.getDate() + 1);
          const wd = d.getDay();
          if (wd !== 0 && wd !== 6) n++;
        }
        return d;
      };
      let fmt;
      try {
        fmt = new Intl.DateTimeFormat(this.data.locale === 'fr' ? 'fr-FR' : 'en-US', { weekday: 'short', day: 'numeric', month: 'short' });
      } catch (e) {
        fmt = { format: (d) => d.toDateString() };
      }
      const text = (el.dataset.template || '').replace('[[from]]', fmt.format(add(min))).replace('[[to]]', fmt.format(add(max)));
      const out = el.querySelector('[data-hp-delivery-text]');
      if (out && text) out.textContent = text;
      else el.hidden = true;
    }

    /* ---------- state ---------- */
    variant(colour, pack) {
      const packName = pack === '2' ? this.data.packTwo : this.data.packOne;
      return this.data.variants.find(
        (v) => v.options[this.data.colourIndex] === colour && v.options[this.data.packIndex] === packName
      );
    }

    onClick(event) {
      const swatch = event.target.closest('[data-hp-colour]');
      if (swatch) {
        event.stopPropagation();
        const colour = swatch.dataset.hpColour;
        const inOffer = swatch.closest('[data-hp-offer]');
        if (swatch.dataset.slot === '2') {
          this.state.c2 = colour;
          this.state.pack = '2';
        } else {
          const followed = this.state.c2 === this.state.c1;
          this.state.c1 = colour;
          if (followed && !inOffer) this.state.c2 = colour;
          if (inOffer) this.state.pack = inOffer.dataset.pack;
          this.showVariantImage(colour);
        }
        this.render();
        return;
      }
      const offer = event.target.closest('[data-hp-offer]');
      if (offer) {
        this.state.pack = offer.dataset.pack;
        this.render();
        return;
      }
      const thumb = event.target.closest('[data-hp-thumb]');
      if (thumb) {
        this.goTo(parseInt(thumb.dataset.index, 10));
        return;
      }
      if (event.target.closest('[data-hp-prev]')) this.goTo(this.state.index - 1);
      if (event.target.closest('[data-hp-next]')) this.goTo(this.state.index + 1);
    }

    setText(sel, text) {
      this.querySelectorAll(sel).forEach((el) => { el.textContent = text; });
    }

    render() {
      const { pack, c1, c2 } = this.state;
      const s = this.data.strings;

      this.querySelectorAll('[data-hp-colour]').forEach((el) => {
        const current = el.dataset.slot === '2' ? c2 : c1;
        el.setAttribute('aria-pressed', el.dataset.hpColour === current ? 'true' : 'false');
      });
      this.setText('[data-hp-colour-name]', c1);
      this.setText('[data-hp-colour2-name]', c2);

      this.querySelectorAll('[data-hp-offer]').forEach((el) => {
        const selected = el.dataset.pack === pack;
        el.classList.toggle('is-selected', selected);
        el.setAttribute('aria-checked', selected ? 'true' : 'false');
      });

      const one = this.variant(c1, '1');
      const two = this.variant(c1, '2');
      if (one) this.setText('[data-hp-offer-price="1"]', one.price);
      if (two) {
        this.setText('[data-hp-offer-price="2"]', two.price);
        this.setText('[data-hp-offer-compare]', two.compare);
        this.setText('[data-hp-offer-each]', s.each.replace('[[each]]', two.each));
        if (two.save) this.setText('[data-hp-offer-save]', s.save.replace('[[amount]]', two.save));
      }

      const chosen = pack === '2' ? two : one;
      if (!chosen) return;

      this.setText('[data-hp-price]', chosen.price);
      const was = this.querySelector('[data-hp-compare]');
      const save = this.querySelector('[data-hp-save]');
      if (was) { was.textContent = chosen.compare; was.hidden = !chosen.compare; }
      if (save) { save.textContent = chosen.save ? s.save.replace('[[amount]]', chosen.save) : ''; save.hidden = !chosen.save; }
      const note = this.querySelector('[data-hp-note]');
      if (note) {
        if (pack === '1' && two) {
          note.textContent = s.nudge.replace('[[total]]', two.price).replace('[[each]]', two.each);
          note.hidden = false;
        } else if (pack === '2' && two) {
          note.textContent = s.each.replace('[[each]]', two.each);
          note.hidden = false;
        } else {
          note.hidden = true;
        }
      }

      const terms = this.querySelector('[data-hp-terms-id]');
      if (terms && terms.value !== String(chosen.id)) {
        terms.value = chosen.id;
        terms.dispatchEvent(new Event('change', { bubbles: true }));
      }

      const ok = chosen.available;
      if (this.atc) {
        this.atc.disabled = !ok;
        this.setText('[data-hp-atc-text]', ok ? s.add : s.soldOut);
        this.setText('[data-hp-atc-price]', chosen.price);
      }
      const label = pack === '2' ? (c1 === c2 ? c1 + ' ×2' : c1 + ' + ' + c2) : c1;
      this.setText('[data-hp-sticky-colour]', label);
      this.setText('[data-hp-sticky-price]', chosen.price);
      const stickyBtn = this.querySelector('[data-hp-sticky-atc]');
      if (stickyBtn) { stickyBtn.disabled = !ok; stickyBtn.textContent = ok ? s.add : s.soldOut; }
      const stickyImg = this.querySelector('[data-hp-sticky-img]');
      if (stickyImg && one && one.image) stickyImg.src = one.image;
    }

    async addToCart() {
      const { pack, c1, c2 } = this.state;
      const v = this.variant(c1, pack);
      if (!v || !v.available) return;
      const item = { id: v.id, quantity: 1 };
      if (pack === '2') item.properties = { [this.data.strings.coloursLabel || 'Device colours']: c1 + ' + ' + c2 };

      const buttons = this.querySelectorAll('[data-hp-atc], [data-hp-sticky-atc]');
      buttons.forEach((b) => { b.disabled = true; b.classList.add('is-loading'); });
      if (this.errorEl) this.errorEl.hidden = true;
      try {
        const response = await fetch((window.Shopify && window.Shopify.routes && window.Shopify.routes.root || '/') + 'cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [item] }),
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.description || data.message || this.data.strings.error);
        document.dispatchEvent(new CustomEvent('wly:cart:changed', { detail: { open: true } }));
      } catch (error) {
        if (this.errorEl) { this.errorEl.textContent = error.message || this.data.strings.error; this.errorEl.hidden = false; }
      } finally {
        buttons.forEach((b) => { b.disabled = false; b.classList.remove('is-loading'); });
      }
    }
  }
  customElements.define('halo-product', HaloProduct);
}

/* Spotlight add-to-cart (home page) */
if (!customElements.get('halo-spot-buy')) {
  class HaloSpotBuy extends HTMLElement {
    connectedCallback() {
      this.qty = 1;
      this.out = this.querySelector('output');
      this.errorEl = this.querySelector('[data-halo-error]');
      this.colour = this.dataset.initial;
      this.map = JSON.parse(this.querySelector('[data-halo-map]').textContent);
      this.addEventListener('click', (event) => {
        if (event.target.closest('[data-q="-"]')) this.setQty(this.qty - 1);
        if (event.target.closest('[data-q="+"]')) this.setQty(this.qty + 1);
        const sw = event.target.closest('[data-halo-colour]');
        if (sw) {
          this.colour = sw.dataset.haloColour;
          this.querySelectorAll('[data-halo-colour]').forEach((el) => el.setAttribute('aria-pressed', el === sw ? 'true' : 'false'));
          const label = this.querySelector('[data-halo-colour-name]');
          if (label) label.textContent = this.colour;
        }
        if (event.target.closest('[data-halo-add]')) this.add();
      });
    }
    setQty(n) { this.qty = Math.max(1, Math.min(10, n)); if (this.out) this.out.textContent = this.qty; }
    async add() {
      const id = this.map[this.colour];
      if (!id) return;
      if (this.errorEl) this.errorEl.hidden = true;
      try {
        const response = await fetch((window.Shopify && window.Shopify.routes && window.Shopify.routes.root || '/') + 'cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [{ id, quantity: this.qty }] }),
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.description || data.message || 'Error');
        document.dispatchEvent(new CustomEvent('wly:cart:changed', { detail: { open: true } }));
      } catch (error) {
        if (this.errorEl) { this.errorEl.textContent = error.message; this.errorEl.hidden = false; }
      }
    }
  }
  customElements.define('halo-spot-buy', HaloSpotBuy);
}
